import { FC, useEffect, useId, useRef, useState } from 'react';
import { Box, Svg, SvgAction, TextField } from '@hopskipdrive/react-components';

import { Container } from './SearchNavBar.styles';

type DebounceHandleQueryOptions = {
  force?: boolean;
  state?: 'active' | 'deactivated' | 'paused' | 'uploads';
};

interface SearchBoxProps {
  id?: string;
  debounceHandleQuery: (query: string, options?: DebounceHandleQueryOptions) => void;
  placeholder: string;
  accessibleLabel?: string;
  query?: string;
}

export const SearchNavBar: FC<SearchBoxProps> = ({ id, debounceHandleQuery, placeholder, accessibleLabel, query }) => {
  const inputId = useId();
  const ref = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(query);

  useEffect(() => {
    if (value !== query) {
      setValue(query);
    }
  }, [query]);

  const handleQuerySearch = (e) => {
    const inputValue = e.target.value;

    // it needs to either have typed something or clearing the input
    if (e && (e.key === 'Enter' || e.code === 'Enter' || e.key === 13) && (inputValue || (query && !inputValue))) {
      debounceHandleQuery(inputValue || '', { force: true });
      e.target.blur();
    }
  };

  return (
    <Container>
      <TextField
        inputRef={ref}
        id={id ?? `search-field-${inputId}`}
        data-testid="search-nav-bar"
        inputProps={{ 'aria-label': accessibleLabel ?? placeholder }}
        size="small"
        variant="outlined"
        InputProps={{
          startAdornment: (
            <Box mr={2}>
              <Svg icon="search" />
            </Box>
          ),
          endAdornment: (
            <div
              role="button"
              tabIndex={ref?.current?.value ? 0 : -1}
              aria-label="Clear search"
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key == ' ') {
                  ref?.current ? (ref.current.value = '') : null;
                  setValue('');
                  debounceHandleQuery('', { force: true });
                }
              }}
              onClick={() => {
                ref?.current ? (ref.current.value = '') : null;
                setValue('');
                debounceHandleQuery('', { force: true });
              }}
            >
              <SvgAction icon="close" display={ref?.current?.value ? '' : 'none'} />
            </div>
          ),
        }}
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleQuerySearch}
      />
    </Container>
  );
};
